// @flow

import React, { useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import BasicHeader from './BasicHeader';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import {
  selectPlanInfo,
  selectProductInfo,
  selectSubscriptionStatus
} from '../../reduxStore/modules/billing';
import { setBilling } from '../../reduxStore/modules/billing/actions';
import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';

import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

import useInterval from '../../hooks/useInterval';

import Logo from '../../assets/askneo-favicon.png';
import ContentContainer from '../SharedComponents/ContentContainer';

import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/CheckoutHome.Style';
import { useTheme } from '@mui/material/styles';

const STEP_LABELS = ['Activate Plan', 'Certify Number'];

const GOOGLE_FORM_BASE_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSfLeXu6UIbw3egbvH13UVaLasTq2m3lPJyr1P5aSDgXiHKVCQ/viewform';
const GOOGLE_FORM_ENTRY_ID = 'entry.1280499415';

const POLL_INTERVAL_MS = 5000;
const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

// Step 0: Activate plan / exit trial
function ExitTrialStep({ planInfo, productInfo, onActivate, loading, error }) {
  const theme = useTheme();
  const classes = createClasses(styles, theme);

  const planName = productInfo?.name || planInfo?.nickname || 'Your Plan';
  const rawAmount = planInfo?.amount || planInfo?.unit_amount || 0;
  const amount = (rawAmount / 100).toFixed(2);
  const interval = planInfo?.interval === 'year' ? 'yearly' : 'monthly';
  const billedLabel = planInfo?.interval === 'year' ? 'Billed yearly' : 'Billed monthly';

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {'Activate your plan to start texting & calling'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {'Begin sending and receiving texts and calls with your business number.'}
      </Typography>

      {/* Order summary */}
      <Box sx={classes.orderSummaryBox} data-testid="order-summary">
        <Box sx={classes.summaryRow}>
          <Typography sx={classes.orderSummaryTitle}>{planName}</Typography>
          <Typography sx={classes.orderSummaryPrice}>
            {`$${amount}/${planInfo?.interval === 'year' ? 'year' : 'mo'}`}
          </Typography>
        </Box>
        <Box sx={classes.summaryRow}>
          <Typography variant="body1" color="text.secondary">{billedLabel}</Typography>
        </Box>
      </Box>

      {/* What happens next */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {'What happens next'}
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
          <li>
            <Typography variant="body2">
              {'Your free trial ends and your plan activates instantly'}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {'Your first charge of '}
              <strong>{`$${amount}`}</strong>
              {' will be billed today'}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {'Start sending and receiving texts & calls right away'}
            </Typography>
          </li>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="activate-error">
          {error}
        </Alert>
      )}

      <Button
        color="primary"
        variant="contained"
        fullWidth
        sx={classes.primaryCta}
        onClick={onActivate}
        disabled={loading}
        data-testid="activate-cta"
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
            {'Activating...'}
          </>
        ) : 'Activate My Plan'}
      </Button>

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1.5 }}>
        {'Cancel anytime from your billing settings. No long-term commitment.'}
      </Typography>
    </Box>
  );
}

// Step 1: Certify number
function CertificationStep({ user }) {
  const history = useHistory();
  const theme = useTheme();
  const classes = createClasses(styles, theme);

  const [isCertified, setIsCertified] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const startTimeRef = useRef(Date.now());

  const accountId = user?.accountId || '';
  const formUrl = accountId
    ? `${GOOGLE_FORM_BASE_URL}?embedded=true&${GOOGLE_FORM_ENTRY_ID}=${encodeURIComponent(accountId)}`
    : `${GOOGLE_FORM_BASE_URL}?embedded=true`;
  const formLinkUrl = accountId
    ? `${GOOGLE_FORM_BASE_URL}?${GOOGLE_FORM_ENTRY_ID}=${encodeURIComponent(accountId)}`
    : GOOGLE_FORM_BASE_URL;

  const pollCallback = useCallback(() => {
    if (isCertified) return;

    if (Date.now() - startTimeRef.current >= TIMEOUT_MS) {
      setTimedOut(true);
      return;
    }

    SPBackend.getCertificationFormStatus(user)
      .then(res => {
        if (res.data?.certified) {
          setIsCertified(true);
        }
      })
      .catch(() => {
        // Silently catch errors, keep polling
      });
  }, [user, isCertified]);

  useInterval(pollCallback, isCertified || timedOut ? null : POLL_INTERVAL_MS);

  return (
    <Box sx={{ pb: 10 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {'One last step — certify your number'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {'Complete the form below so we can verify and activate your business number.'}
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {'Certification typically takes up to 7 business days.'}
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {'Having trouble loading the form? '}
        <a href={formLinkUrl} target="_blank" rel="noopener noreferrer">
          {'Open it in a new tab'}
        </a>
      </Typography>

      <iframe
        src={formUrl}
        width="100%"
        height="800"
        frameBorder="0"
        title="Number Certification Form"
        style={{ border: 'none' }}
        data-testid="certification-iframe"
      />

      {isCertified ? (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
          p: 2,
          zIndex: 1300,
        }}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            sx={{ ...classes.primaryCta, maxWidth: 480, mx: 'auto', display: 'flex' }}
            onClick={() => history.push('/member')}
            data-testid="go-to-dashboard"
          >
            {'Done — Take Me to My Dashboard'}
          </Button>
        </Box>
      ) : timedOut ? (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
          p: 2,
          zIndex: 1300,
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
            {"If you've already submitted the form, click below to continue."}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            sx={{ ...classes.primaryCta, maxWidth: 480, mx: 'auto', display: 'flex' }}
            onClick={() => history.push('/member')}
            data-testid="go-to-dashboard-fallback"
          >
            {'Continue to Dashboard'}
          </Button>
        </Box>
      ) : (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
          p: 2,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary" data-testid="waiting-for-submission">
            {'Waiting for form submission...'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Main component
function ActivateHome() {
  const theme = useTheme();
  const classes = createClasses(styles, theme);
  const history = useHistory();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const planInfo = useSelector(selectPlanInfo);
  const productInfo = useSelector(selectProductInfo);
  const subscriptionStatus = useSelector(selectSubscriptionStatus);

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasActivated = useRef(false);

  // Guard: redirect if not trialing (skip when user just activated via this page)
  if (!hasActivated.current && subscriptionStatus && subscriptionStatus !== 'trialing') {
    history.replace('/member');
    return null;
  }

  const handleActivate = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await SPBackend.activateSubscription(user);
      hasActivated.current = true;
      setCurrentStep(1);
      dispatch(setBilling(response.data.billing));
    } catch (err) {
      log('[ActivateHome] activation error:', err);
      const message = err.response?.data?.message
        || err.response?.data
        || err.message
        || 'Activation failed. Please try again.';
      setError(typeof message === 'string' ? message : 'Activation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{'Neo | Activate Your Account'}</title>
      </Helmet>
      <BasicHeader title="Activate" />
      <ContentContainer hasSubheader={false}>
        <Box sx={classes.contentCtr}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <a href="/">
              <img height={40} src={Logo} alt="AskNeo Logo" />
            </a>
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
              {'Certify your number and activate your account'}
            </Typography>
            <Stepper
              activeStep={currentStep}
              sx={classes.checkoutStepper}
              alternativeLabel
            >
              {STEP_LABELS.map((label, index) => (
                <Step key={label} completed={index < currentStep}>
                  <StepButton
                    onClick={() => {
                      if (index < currentStep) setCurrentStep(index);
                    }}
                    disabled={index > currentStep}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Paper sx={classes.formPaper}>
            <Box sx={classes.stripeCtr}>
              {currentStep === 0 ? (
                <ExitTrialStep
                  planInfo={planInfo}
                  productInfo={productInfo}
                  onActivate={handleActivate}
                  loading={loading}
                  error={error}
                />
              ) : (
                <CertificationStep user={user} />
              )}
            </Box>
          </Paper>
        </Box>
      </ContentContainer>
    </Box>
  );
}

export default ActivateHome;
